import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard, PlusCircle, Table as TableIcon, CheckCircle2, Calendar,
  BarChart3, Settings, Users, ScrollText, LogOut, Menu, X, Store as StoreIcon, Building2,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/nuovo-giorno", label: "Nuovo Giorno", icon: PlusCircle },
  { to: "/registro", label: "Registro", icon: TableIcon },
  { to: "/riconciliazioni", label: "Riconciliazioni", icon: CheckCircle2 },
  { to: "/storico", label: "Storico Mesi", icon: Calendar },
  { to: "/report", label: "Report", icon: BarChart3 },
];
const ADMIN_NAV = [
  { to: "/punti-vendita", label: "Punti Vendita", icon: Building2 },
  { to: "/utenti", label: "Utenti", icon: Users },
  { to: "/audit", label: "Audit", icon: ScrollText },
];
const TAIL = [{ to: "/impostazioni", label: "Impostazioni", icon: Settings }];

function NavItems({ onNavigate, isSuper }) {
  const items = [...NAV, ...(isSuper ? ADMIN_NAV : []), ...TAIL];
  return (
    <nav className="flex flex-col gap-1 p-3">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate}
            data-testid={`nav-${it.to === "/" ? "dashboard" : it.to.replace("/", "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors duration-200 ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}>
            <Icon className="h-5 w-5 shrink-0" />
            <span className="uppercase tracking-wide text-[13px]">{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function StoreSwitcher({ compact }) {
  const { stores, storeId, setStoreId, isSuperAdmin } = useStore();
  return (
    <Select value={storeId} onValueChange={setStoreId}>
      <SelectTrigger className={`gap-2 border-primary/40 bg-secondary ${compact ? "h-10 w-full" : "h-11 w-[240px]"}`} data-testid="store-switcher">
        <StoreIcon className="h-4 w-4 text-primary" />
        <SelectValue placeholder="Seleziona sede" />
      </SelectTrigger>
      <SelectContent>
        {isSuperAdmin && <SelectItem value="all" data-testid="store-opt-all">🏢 Tutti i punti vendita</SelectItem>}
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`store-opt-${s.id}`}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { currentStore, storeId, isSuperAdmin } = useStore();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Brand = (
    <div className="flex items-center gap-3 border-b border-border px-5 py-4">
      <img src="/logo_icon.png" alt="Petrol Giuly" className="h-10 w-10 rounded-lg" />
      <div>
        <div className="font-display text-lg font-extrabold leading-none tracking-tight">PETROL GIULY</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Punti vendita · incassi</div>
      </div>
    </div>
  );

  const storeBadge = (
    <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm">
      <StoreIcon className="h-4 w-4 text-primary" />
      <span className="font-semibold text-primary" data-testid="current-store-label">
        {storeId === "all" ? "Tutti i punti vendita" : (currentStore?.name || "—")}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-background enilive-grid-bg">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        {Brand}
        <div className="flex-1 overflow-y-auto"><NavItems isSuper={isSuperAdmin} /></div>
        <div className="border-t border-border p-4">
          <div className="mb-3 px-1">
            <div className="text-sm font-semibold">{user?.name || user?.username}</div>
            <div className="text-xs uppercase tracking-wide text-primary">{(user?.role || "").replace("_", " ")}</div>
          </div>
          <Button variant="secondary" className="w-full gap-2" onClick={doLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Esci
          </Button>
        </div>
      </aside>

      {/* Top bar (both) */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-card/95 px-4 py-3 backdrop-blur-md lg:pl-[17.5rem]">
        <div className="flex items-center gap-2 lg:hidden">
          <img src="/logo_icon.png" alt="PG" className="h-9 w-9 rounded-lg" />
          <span className="font-display text-base font-extrabold">PETROL GIULY</span>
        </div>
        <div className="hidden lg:block">{storeBadge}</div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:block"><StoreSwitcher /></div>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </header>

      {/* mobile store switcher row */}
      <div className="border-b border-border bg-card/60 px-4 py-2 sm:hidden">
        <StoreSwitcher compact />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col border-r border-border bg-card">
            <div className="flex items-center justify-between">
              {Brand}
              <Button variant="ghost" size="icon" className="mr-2" onClick={() => setOpen(false)}><X className="h-6 w-6" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto"><NavItems isSuper={isSuperAdmin} onNavigate={() => setOpen(false)} /></div>
            <div className="border-t border-border p-4">
              <Button variant="secondary" className="w-full gap-2" onClick={doLogout}><LogOut className="h-4 w-4" /> Esci</Button>
            </div>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 lg:px-10 lg:py-8">{children}</div>
      </main>

      {storeId !== "all" && (
        <button onClick={() => navigate("/nuovo-giorno")} data-testid="mobile-fab-nuovo-giorno"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-5 py-4 font-bold text-primary-foreground shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-105 lg:hidden">
          <PlusCircle className="h-6 w-6" /> Giornata
        </button>
      )}
    </div>
  );
}
